import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./App.css";
import TopNav, { TabId } from "./components/TopNav";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import KPICard from "./components/KPICard";
import VarianceChart from "./components/VarianceChart";
import TrendChart from "./components/TrendChart";
import StrategicInsights from "./components/StrategicInsights";
import ApprovalStatus from "./components/ApprovalStatus";
import VarianceExplorer from "./components/VarianceExplorer";
import FinancialIntelligence from "./components/FinancialIntelligence";
import ExecutiveSummary from "./components/ExecutiveSummary";
import AuditView from "./components/Audit";
import CopilotChat from "./components/CopilotChat";
import {
  useFilters,
  useHeader,
  useKpis,
  useVarianceChart,
  useInsights,
  useApprovals,
} from "./hooks/useDashboardData";

function App() {
  const [activeTab, setActiveTab] = useState<TabId>("global-insights");
  const [pendingFilters, setPendingFilters] = useState<any>(null);
  const [appliedFilters, setAppliedFilters] = useState<any>(null);
  const [initialQuarter, setInitialQuarter] = useState<string>("All");
  const veRef = useRef<HTMLDivElement>(null);

  // ── Load filter options from API ───────────────────────────────────────────────
  const { data: filterOptions, isLoading: filtersLoading } = useFilters();

  // ── Derive initial filter values once filter options arrive ────────────────────
  useEffect(() => {
    if (filterOptions && !appliedFilters) {
      const initial: Record<string, string> = {};
      (filterOptions.dropdowns as any[]).forEach((d: any) => {
        initial[d.id] = d.selected ?? d.options[0];
      });
      setPendingFilters(initial);
      setAppliedFilters(initial);
    }
  }, [filterOptions, appliedFilters]);

  // ── React Query data hooks (dashboard) ────────────────────────────────────────
  const headerQ        = useHeader(appliedFilters);
  const kpisQ          = useKpis(appliedFilters);
  const varianceChartQ = useVarianceChart(appliedFilters);
  const insightsQ      = useInsights(appliedFilters);
  const approvalsQ     = useApprovals(appliedFilters);

  // ── Filter handlers ───────────────────────────────────────────────────────────
  const handlePendingChange = (id: string, value: string) => {
    setPendingFilters((prev: any) => ({ ...(prev ?? {}), [id]: value }));
  };

  const handleApply = () => {
    if (pendingFilters) setAppliedFilters({ ...pendingFilters });
  };

  const handleReset = () => {
    if (appliedFilters) setPendingFilters({ ...appliedFilters });
  };

  const handleResetToDefault = () => {
    if (!filterOptions) return;
    const initial: Record<string, string> = {};
    (filterOptions.dropdowns as any[]).forEach((d: any) => {
      initial[d.id] = d.selected ?? d.options[0];
    });
    setPendingFilters(initial);
    setAppliedFilters(initial);
  };

  const handleGlAccountSelect = (glAccount: string) => {
    setPendingFilters((prev: any) => ({ ...(prev ?? {}), glAccount }));
  };

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
  };

  // ── Export Variance Explorer as PDF ───────────────────────────────────────────
  const handleExportPdf = async () => {
    const el = veRef.current;
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [canvas.width / 2, canvas.height / 2] });
    pdf.addImage(imgData, "PNG", 0, 0, canvas.width / 2, canvas.height / 2);
    pdf.save(`variance-explorer-FY${appliedFilters?.fiscalYear ?? "report"}.pdf`);
  };

  // ── Loading / error states ────────────────────────────────────────────────────
  const dashboardLoading =
    filtersLoading ||
    !appliedFilters ||
    headerQ.isLoading ||
    kpisQ.isLoading ||
    varianceChartQ.isLoading;

  const anyError =
    headerQ.error || kpisQ.error || varianceChartQ.error;

  // ── Initial load gate ─────────────────────────────────────────────────────────
  if (filtersLoading || !filterOptions || !pendingFilters || !appliedFilters) {
    return (
      <div className="app-loading">
        <div className="app-loading-text">Loading dashboard data from Snowflake…</div>
      </div>
    );
  }

  return (
    <div className="app-wrapper">
      {/* ── Top navigation bar ──────────────────────────────────────────────── */}
      <TopNav activeTab={activeTab} onTabChange={handleTabChange} />

      {/* ── Body: sidebar + main content ───────────────────────────────────── */}
      <div className="app-body">
        {/* Hide sidebar on Financial Intelligence and Audit screens */}
        {activeTab !== "financial-intelligence" && activeTab !== "audit" && (
          <Sidebar
            filterOptions={filterOptions}
            pendingFilters={pendingFilters}
            appliedFilters={appliedFilters}
            onPendingChange={handlePendingChange}
            onApply={handleApply}
            onReset={handleReset}
            showExportPdf={activeTab === "variance-explorer"}
            onExportPdf={handleExportPdf}
          />
        )}

        <div className="main-content">
          {activeTab === "variance-explorer" ? (
            /* ── Variance Explorer view ───────────────────────────────────── */
            <div ref={veRef} style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <VarianceExplorer
                filters={appliedFilters}
                initialQuarter={initialQuarter}
                onGlAccountSelect={handleGlAccountSelect}
                onBack={() => { setActiveTab("global-insights"); setInitialQuarter("All"); }}
              />
            </div>
          ) : activeTab === "financial-intelligence" ? (
            /* ── Financial Intelligence view ──────────────────────────────── */
            <FinancialIntelligence filters={appliedFilters} />
          ) : activeTab === "executive-summary" ? (
            /* ── Executive Summary view ───────────────────────────────────── */
            <ExecutiveSummary
              filters={appliedFilters}
            />
          ) : activeTab === "audit" ? (
            /* ── Audit view ────────────────────────────────────────────────── */
            <AuditView />
          ) : (
            /* ── Global Insights / Dashboard view ─────────────────────────── */
            <>
              {dashboardLoading ? (
                <div className="content-loading">
                  Loading…
                </div>
              ) : anyError ? (
                <div className="content-error">
                  <div style={{ fontSize: 28, marginBottom: 10 }}>⚠️</div>
                  Unable to reach the backend. Please ensure the Node server is running on port 4000.
                </div>
              ) : (
                <>
                  <Header data={headerQ.data} />
                  <div className="content-area">
                    {/* KPI cards */}
                    <div className="kpi-row">
                      {kpisQ.data?.map((kpi: any, i: number) => (
                        <KPICard key={i} data={kpi} />
                      ))}
                    </div>

                    {/* Charts */}
                    <div className="charts-row">
                      {varianceChartQ.data && (
                        <div className="chart-card">
                          <VarianceChart
                            data={varianceChartQ.data}
                            onDrillDown={(quarter?: string) => {
                              setInitialQuarter(quarter ?? "All");
                              setActiveTab("variance-explorer");
                            }}
                          />
                        </div>
                      )}
                      <div className="chart-card">
                        <TrendChart
                          filters={appliedFilters}
                          categoryOptions={
                            (filterOptions?.dropdowns as any[])
                              ?.find((d: any) => d.id === "accountCategory")?.options
                            ?? ["All", "EXPENSE", "REVENUE"]
                          }
                        />
                      </div>
                    </div>

                    {/* Bottom row */}
                    <div className="bottom-row">
                      {insightsQ.data && <StrategicInsights data={insightsQ.data} />}
                      {approvalsQ.data && <ApprovalStatus data={approvalsQ.data} />}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <CopilotChat />
    </div>
  );
}

export default App;
